import * as React from "react";
import {
  isValidContactNumber,
  resetErrorFieldStyle,
  txtFormatChecker,
  isNumber,
} from "../Variables";
import { useContextStateManagement } from "../context/ContextApi";
import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { logout } from "../features/userSlice";
import { Link, useNavigate } from "react-router-dom";
import { TextField, Autocomplete } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { TimeZoneDropdown } from "./TimeZoneDropdown";
import { BusinessHoursPicker } from "./BusinessHoursPicker";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import { ToolsMultiSelectDropdown } from "./ToolsMultiSelectDropdown";
import { LlmReasoningControls } from "./LlmReasoningControls";
import {
  DEFAULT_REASONING_EFFORT,
  normalizeReasoningSelection,
} from "./llmReasoningOptions";
import "../components/Auth/Auth.css";

const PAUTHQ_PROVIDER_ID_STORAGE_KEY = "cm_paAuthQ_providerId";

// "claude-sonnet-5" also matches Sonnet 5.5. Grok 4.7 is deliberately absent: Bedrock rejects
// explicit cache points for it and caches its prompts automatically.
const supportsBedrockPromptCaching = (model = "") =>
  model.includes("claude-haiku-4-5") ||
  model.includes("claude-haiku-5-5") ||
  model.includes("claude-sonnet-4-6") ||
  model.includes("claude-sonnet-5");

const LEGACY_SONNET_4_MODEL = "us.anthropic.claude-sonnet-4-20250514-v1:0";
const SONNET_4_6_MODEL = "us.anthropic.claude-sonnet-4-6";

const normalizeBedrockModelId = (model = "") =>
  model === LEGACY_SONNET_4_MODEL ? SONNET_4_6_MODEL : model;

const isFluxSttModel = (model = "") =>
  String(model).toLowerCase().includes("flux");

const INBOUND_PROMPT_STEPS = [
  { id: 1, label: "Step 1", title: "Basic Details" },
  { id: 2, label: "Step 2", title: "Configuration" },
  { id: 3, label: "Step 3", title: "Model & Publish" },
];

export const PAuthQOnBoardingForm = () => {
  const DEFAULT_MODAL = "us.anthropic.claude-haiku-4-5-20251001-v1:0";
  const DEFAULT_VOICE_PROVIDER = "Deepgram";
  const DEFAULT_VOICE_LABEL = "Har (Female)";
  const DEFAULT_STATUS = "active";
  const BACKGROUND_NOISE_PLACEHOLDER = "__select_background_noise__";
  const NO_BACKGROUND_NOISE = "";
  const DEFAULT_BACKGROUND_NOISE = BACKGROUND_NOISE_PLACEHOLDER;
  const DEFAULT_SIP_TERMINATION_SECONDS = "900";
  const DEFAULT_TRANSFER_SIP_TERMINATION_SECONDS = "900";
  const DEFAULT_LANGUAGES = ["English"];
  const {
    PracticeId,
    dateRangeRequest,
    userProfession,
    practiceName,
    token,
    usersPostRequest,
    setToken,
    fetchUserRecord,
    activeOnBoardingTem,
    activeTab,
    userType,
    setUserType,
    inboundContact,
  } = useContextStateManagement();
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promptFile, setPromptFile] = useState("");
  const [providerId, setProviderId] = useState("");
  const [providerName, setProviderName] = useState("");
  const [providerFacilityName, setProviderFacilityName] = useState("");
  const [providerContactNumber, setProviderContactNumber] = useState("");
  // const [nationalProviderIdentifier, setNationalProviderIdentifier] = useState("");
  // const [providerFaxNumber, setProviderFaxNumber] = useState("");
  // const [apiKeys, setApiKeys] = useState("");
  const [promptFiledownloadLink, setPromptFiledownloadLink] = useState("");
  const [providerData, setProviderData] = useState([]);
  const [selectedModal, setSelectedModal] = useState(DEFAULT_MODAL);
  const [selectedVoice, setSelectedVoice] = useState(DEFAULT_VOICE_PROVIDER);
  const [firstMessage, setFirstMessage] = useState("");
  const [lastMessage, setLastMessage] = useState("");
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [parameters, setParameters] = useState([]);
  const [voiceModals, setVoiceModals] = useState([]);
  const [selectedVoiceModals, setSelectedVoiceModals] = useState([]);
  const [selectedVoiceModal, setSelectedVoiceModal] = useState("");
  const sttModelOptions = [
    { label: "Deepgram Flux (General)", value: "flux-general-en" },
    { label: "Deepgram Nova-2 (General)", value: "nova-2-general" },
    { label: "Deepgram Nova-2 (Phonecall)", value: "nova-2-phonecall" },
    { label: "Deepgram Nova-3 (General)", value: "nova-3-general" },
    { label: "Deepgram Nova-3 (Medical)", value: "nova-3-medical" },
  ];
  const [selectedSttModel, setSelectedSttModel] = useState(
    sttModelOptions[0].value
  );
  const [fluxKeyterms, setFluxKeyterms] = useState("");
  const [voices, setVoices] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState("");
  const [businessHoursDays, setBusinessHoursDays] = useState([]);
  const [businessHoursStart, setBusinessHoursStart] = useState("");
  const [businessHoursEnd, setBusinessHoursEnd] = useState("");
  const [backgroundNoise, setBackgroundNoise] = useState(
    DEFAULT_BACKGROUND_NOISE
  );
  const [sipTerminationDynamicSeconds, setSipTerminationDynamicSeconds] =
    useState(DEFAULT_SIP_TERMINATION_SECONDS);
  const [transferSipTerminationDynamicSeconds, setTransferSipTerminationDynamicSeconds] =
    useState(DEFAULT_TRANSFER_SIP_TERMINATION_SECONDS);
  const [cacheEnabled, setCacheEnabled] = useState(false);
  const [greetingInterruptible, setGreetingInterruptible] = useState(false);
  const [cacheTTL, setCacheTTL] = useState(300);
  const [thinkingEnabled, setThinkingEnabled] = useState(false);
  const [reasoningEffort, setReasoningEffort] = useState(DEFAULT_REASONING_EFFORT);
  const [eouMinDelay, setEouMinDelay] = useState(0.3);
  const [eouMaxDelay, setEouMaxDelay] = useState(0.8);
  const [turnDetectionMode, setTurnDetectionMode] = useState("vad");
  const [sttProvider, setSttProvider] = useState("Deepgram");
  const [elevenLabsTtsModel, setElevenLabsTtsModel] = useState("eleven_flash_v2_5");
  const [ivrEnabled, setIvrEnabled] = useState(false);
  const [ivrOptions, setIvrOptions] = useState([]);
  const [multiAgentEnabled, setMultiAgentEnabled] = useState(false);
  const [agentConfigs, setAgentConfigs] = useState([]);
  const [apiFunctions, setApiFunctions] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState(
    DEFAULT_LANGUAGES
  );
  // Intentionally plain useState (not usePersistedState): the step index
  // should always start at "Basic Details" on a fresh visit to Prompt Hub,
  // not resume wherever the user last left off in a previous session.
  const [currentStep, setCurrentStep] = useState(1);
  const restoredProviderRef = useRef(false);
  // const optionsForLanguages = ["English", "Spanish", "Multilingual"];
  const optionsForLanguages = ["English", "Spanish"];

  // Knowledge Base state
  const [kbDocuments, setKbDocuments] = useState([]);
  const [kbUsagePrompt, setKbUsagePrompt] = useState("");
  const [kbFile, setKbFile] = useState(null);
  const [kbUploading, setKbUploading] = useState(false);
  const [kbSavingPrompt, setKbSavingPrompt] = useState(false);
  const [kbError, setKbError] = useState("");

  // New state for phone number selection
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [allNumbers, setAllNumbers] = useState([]);
  const [selectedNumberSid, setSelectedNumberSid] = useState("");
  const legacyNumberOption = React.useMemo(() => {
    if (!providerContactNumber) return null;
    const existing = allNumbers.find(
      (n) => n.phoneNumber === providerContactNumber
    );
    if (existing) return existing;
    // Legacy static number that is not part of the Twilio inventory
    return {
      phoneNumber: providerContactNumber,
      sid: "",
      legacy: true,
    };
  }, [providerContactNumber, allNumbers]);

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchAvailableNumbers = async () => {
      if (PracticeId && token) {
        const data = await fetchUserRecord(token, `/api/getPurchasedPhoneNumbers?practiceId=${PracticeId}`);
        if (data && Array.isArray(data)) {
          setAllNumbers(data);
          // Filter for numbers that aren't already configured as trunks (matching PhoneNum.js logic),
          // and aren't already owned by a different practice on this shared Twilio account (a number
          // can look "unconfigured" to Twilio right after another practice's trunk was torn down).
          const unconfigured = data.filter(n => !n.trunkSid && !n.ownedByOtherPractice && (!n.voiceUrl || n.voiceUrl === "https://demo.twilio.com/welcome/voice/"));
          setAvailableNumbers(unconfigured);
        }
      }
    };
    fetchAvailableNumbers();
  }, [PracticeId, token]);
  const navigate = useNavigate();
  let formData = new FormData();
  let isAnyUploadFieldEmpty = false;
  let isValidDetails = false;

  const getElement = (id) => document.getElementById(id);
  const enableElement = (id) => getElement(id)?.removeAttribute("disabled");
  const disableElement = (id) =>
    getElement(id)?.setAttribute("disabled", "true");
  const setElementValue = (id, value) => {
    const element = getElement(id);
    if (element) {
      element.value = value;
    }
  };
  const addElementClass = (id, className) =>
    getElement(id)?.classList.add(className);

  const formatFluxKeyterms = (value) => {
    if (Array.isArray(value)) {
      return value.join("\n");
    }
    return typeof value === "string" ? value : "";
  };

  // Some stored rows have a JSON-array string ("[\"English\"]") sitting as a
  // single array element instead of a plain language name, from an older
  // write path that double-encoded this field. Unwrap those so the chip
  // shows "English" instead of the literal JSON text.
  const normalizeSelectedLanguages = (value) => {
    if (!Array.isArray(value)) return value;
    return value.flatMap((entry) => {
      if (typeof entry !== "string") return [entry];
      const trimmed = entry.trim();
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return parsed.map((v) => String(v));
        } catch (_) {
          // not valid JSON, keep the original string
        }
      }
      return [entry];
    });
  };
  const isFluxKeytermEnabled = isFluxSttModel(selectedSttModel);
  const isBedrockCacheAvailable = supportsBedrockPromptCaching(selectedModal);
  const kbActiveDocumentCount = kbDocuments.filter(
    (doc) => String(doc.Status || "").toLowerCase() === "active"
  ).length;
  const kbPanelStyle = {
    background: "rgba(247, 245, 242, 0.72)",
    border: "1px solid rgba(65, 60, 88, 0.08)",
    borderRadius: "22px",
    padding: "24px",
    boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.42)",
  };
  const kbCardStyle = {
    background: "rgba(255, 255, 255, 0.88)",
    border: "1px solid rgba(65, 60, 88, 0.08)",
    borderRadius: "18px",
    padding: "18px",
  };
  const kbSectionHeadingStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    color: "#280137",
    fontSize: "1rem",
    fontWeight: 600,
    marginBottom: "10px",
  };
  const kbBadgeStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    borderRadius: "999px",
    border: "1px solid rgba(65, 60, 88, 0.12)",
    background: "rgba(99, 201, 220, 0.08)",
    color: "#280137",
    fontSize: "0.76rem",
    fontWeight: 600,
    padding: "6px 12px",
  };
  const kbSecondaryTextStyle = {
    color: "#6d5f83",
    fontSize: "0.84rem",
    lineHeight: 1.65,
    marginBottom: 0,
  };
  const kbGhostButtonStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    minWidth: "150px",
    padding: "10px 16px",
    borderRadius: "14px",
    border: "1px solid rgba(65, 60, 88, 0.12)",
    background: "rgba(40, 1, 55, 0.06)",
    color: "#280137",
    fontSize: "0.88rem",
    fontWeight: 600,
    cursor: "pointer",
  };
  const kbTextAreaStyle = {
    resize: "vertical",
    minHeight: "150px",
    borderRadius: "16px",
    border: "1px solid rgba(65, 60, 88, 0.12)",
    boxShadow: "none",
  };

  useEffect(() => {
    if (!isBedrockCacheAvailable && cacheEnabled) {
      setCacheEnabled(false);
    }
  }, [isBedrockCacheAvailable, cacheEnabled]);

  // Keep thinking/effort valid for the selected model (e.g. effort above High is cleared
  // when Claude thinking is turned off; Grok always reasons).
  useEffect(() => {
    const normalized = normalizeReasoningSelection(selectedModal, thinkingEnabled, reasoningEffort);
    if (normalized.thinkingEnabled !== thinkingEnabled) setThinkingEnabled(normalized.thinkingEnabled);
    if (normalized.reasoningEffort !== reasoningEffort) setReasoningEffort(normalized.reasoningEffort);
  }, [selectedModal, thinkingEnabled, reasoningEffort]);

  const handleTimezoneChange = (e) => {
    setSelectedTimezone(e.target.value);
  };

  function resetInputFieldsColor() {
    document
      .querySelectorAll(
        "input.invalid-details, textarea.invalid-details, select.invalid-details, .invalid-details"
      )
      .forEach((element) => {
        element?.classList?.remove("invalid-details");
      });

    const selectedLanguagesElement = document.querySelector(
      ".autocomplete_languages"
    );
    if (selectedLanguagesElement) {
      selectedLanguagesElement.classList.remove("invalid-details");
    }
  }

  const fetchApiHandle = async () => {
    let url = `/api/PAuthQOnBoardingTemplate/${PracticeId}`;
    const userData = await fetchUserRecord(token, url);
    if (userData == "Token is expired, login again") {
      setIsTokenExpired(true);
    } else if (
      userData &&
      userData.providersData &&
      userData.providersData.length === 0
    ) {
      setProviderData([]);
    } else if (userData && userData.providersData) {
      const normalizedProviders = userData.providersData.map((provider) => {
        const sttModel =
          provider.STTModel &&
            sttModelOptions.find((option) => option.value === provider.STTModel)
            ? provider.STTModel
            : sttModelOptions[0].value;
        return {
          ...provider,
          STTModel: sttModel,
        };
      });
      setProviderData(normalizedProviders);
    }
    setApiFunctions(userData && userData.functions ? userData.functions : []);
  };

  const fetchVoiceAndModals = async () => {
    let url = `/api/getVoiceModals`;
    const userData = await fetchUserRecord(token, url);
    if (userData == "Token is expired, login again") {
      setIsTokenExpired(true);
    } else if (userData == "No records") {
      setVoiceModals([]);
    } else {
      filterVoices(userData);
      setVoiceModals(userData);
    }
  };

  const filterVoices = (voiceModalsArray) => {
    let uniqueVoices = {};
    voiceModalsArray.forEach((obj) => {
      uniqueVoices[obj.CategoryLabel] = obj;
    });

    // Define custom sort order
    const customOrder = ["Modal - D", "Modal - E", "Modal - C"];

    // Sort based on the custom order
    let voices = Object.values(uniqueVoices).sort((a, b) => {
      return (
        customOrder.indexOf(a.CategoryLabel) -
        customOrder.indexOf(b.CategoryLabel)
      );
    });

    setVoices(voices);
  };

  const getVoiceCategory = (voiceProvider) =>
    voiceProvider == "Eleven Labs"
      ? "Modal - E"
      : voiceProvider == "Deepgram"
        ? "Modal - D"
        : "Modal - C";

  const getDefaultVoiceModalValue = (
    voiceProvider = DEFAULT_VOICE_PROVIDER,
    availableVoiceModals = voiceModals
  ) => {
    const category = getVoiceCategory(voiceProvider);
    const preferredVoice = availableVoiceModals.find(
      (voice) =>
        voice.CategoryLabel === category &&
        voice.SubCategoryLabel === DEFAULT_VOICE_LABEL &&
        voice.SubCategoryValue
    );

    if (preferredVoice) {
      return preferredVoice.SubCategoryValue;
    }

    const fallbackVoice = availableVoiceModals.find(
      (voice) => voice.CategoryLabel === category && voice.SubCategoryValue
    );

    return fallbackVoice ? fallbackVoice.SubCategoryValue : "";
  };

  const filterVoiceModals = () => {
    const currentSelectedVoice = getVoiceCategory(selectedVoice);
    const filterVoiceModals =
      voiceModals.length > 0
        ? voiceModals.filter(
          (voice) => voice.CategoryLabel == currentSelectedVoice
        )
        : "";
    setSelectedVoiceModals(filterVoiceModals);
  };

  const handleSelectedVoice = (e) => {
    setSelectedVoice(e.target.value);
    setSelectedVoiceModal("");
  };

  const resetForm = () => {
    setProviderName("");
    // setNationalProviderIdentifier("");
    // setProviderFaxNumber("");
    // setApiKeys("");
    setProviderContactNumber("");
    setProviderId("");
    setSelectedNumberSid("");
    setPromptFiledownloadLink("");
    setPromptFile("");
    setSelectedModal(DEFAULT_MODAL);
    setSelectedVoice(DEFAULT_VOICE_PROVIDER);
    setSelectedSttModel(sttModelOptions[0].value);
    setFluxKeyterms("");
    setFirstMessage("");
    setLastMessage("");
    setStatus(DEFAULT_STATUS);
    setParameters([]);
    setSelectedVoiceModal(getDefaultVoiceModalValue(DEFAULT_VOICE_PROVIDER));
    setSelectedTimezone("");
    setBusinessHoursDays([]);
    setBusinessHoursStart("");
    setBusinessHoursEnd("");
    setCacheEnabled(false);
    setGreetingInterruptible(false);
    setCacheTTL(300);
    setThinkingEnabled(false);
    setReasoningEffort(DEFAULT_REASONING_EFFORT);
    setEouMinDelay(0.3);
    setEouMaxDelay(0.8);
    setTurnDetectionMode("vad");
    setSttProvider("Deepgram");
    setElevenLabsTtsModel("eleven_flash_v2_5");
    setIvrEnabled(false);
    setIvrOptions([]);
    setMultiAgentEnabled(false);
    setAgentConfigs([]);
    setBackgroundNoise("");
    setSipTerminationDynamicSeconds("");
    setTransferSipTerminationDynamicSeconds("");
    setSelectedLanguages([]);
    setBackgroundNoise(DEFAULT_BACKGROUND_NOISE);
    setSipTerminationDynamicSeconds(DEFAULT_SIP_TERMINATION_SECONDS);
    setTransferSipTerminationDynamicSeconds(DEFAULT_TRANSFER_SIP_TERMINATION_SECONDS);
    setSelectedLanguages(DEFAULT_LANGUAGES);
    setKbDocuments([]);
    setKbUsagePrompt("");
    setKbFile(null);
    setKbError("");
    resetInputFieldsColor();
    setElementValue("selected_Provider", "");
  };

  const updateForm = (e) => {
    let pId = e.target.value;

    if (pId) {
      const selectedProvider =
        providerData.length > 0
          ? providerData.filter((provider) => provider.Id == pId)
          : "";
      const selectedProviderData = selectedProvider[0];
      const nextSelectedVoice =
        selectedProviderData.SelectedVoice || DEFAULT_VOICE_PROVIDER;
      setProviderId(selectedProviderData.Id);
      sessionStorage.setItem(PAUTHQ_PROVIDER_ID_STORAGE_KEY, String(selectedProviderData.Id));
      setProviderName(selectedProviderData.ProviderName);
      // setProviderFacilityName(selectedProviderData.ProviderFacilityName);
      // setNationalProviderIdentifier(selectedProviderData.NationalProviderIdentifier);
      // setProviderFaxNumber(selectedProviderData.ProviderFaxNumber);
      // setApiKeys(selectedProviderData.API_Keys ? selectedProviderData.API_Keys : "");
      setProviderContactNumber(
        selectedProviderData.ProviderContactNumber.replace(/[-_]/g, "")
      );
      // Find the SID for the existing number from allNumbers
      const existingNumber = allNumbers.find(n => n.phoneNumber === selectedProviderData.ProviderContactNumber.replace(/[-_]/g, ""));
      setSelectedNumberSid(existingNumber ? existingNumber.sid : "");
      setStatus(
        selectedProviderData.Status ? selectedProviderData.Status : DEFAULT_STATUS
      );
      setParameters(
        selectedProviderData.SummaryParameters &&
          selectedProviderData.SummaryParameters !== "" &&
          selectedProviderData.SummaryParameters !== "[]" &&
          Array.isArray(selectedProviderData.SummaryParameters)
          ? selectedProviderData.SummaryParameters
          : []
      );
      setFirstMessage(
        selectedProviderData.FirstMessage
          ? selectedProviderData.FirstMessage
          : ""
      );
      setLastMessage(
        selectedProviderData.LastMessage ? selectedProviderData.LastMessage : ""
      );
      setSelectedModal(
        selectedProviderData.SelectedModal
          ? normalizeBedrockModelId(selectedProviderData.SelectedModal)
          : DEFAULT_MODAL
      );
      setSelectedVoice(nextSelectedVoice);
      setSelectedSttModel(
        selectedProviderData.STTModel &&
          sttModelOptions.find(
            (option) => option.value === selectedProviderData.STTModel
          )
          ? selectedProviderData.STTModel
          : sttModelOptions[0].value
      );
      setFluxKeyterms(formatFluxKeyterms(selectedProviderData.FluxKeyterms));
      setSelectedVoiceModal(
        selectedProviderData.SelectedVoiceModal
          ? selectedProviderData.SelectedVoiceModal
          : getDefaultVoiceModalValue(nextSelectedVoice)
      );
      setPromptFiledownloadLink(
        selectedProviderData.PromptFileUrl
          ? selectedProviderData.PromptFileUrl
          : ""
      );
      setSelectedTimezone(
        selectedProviderData.Timezone ? selectedProviderData.Timezone : ""
      );
      setBusinessHoursDays(selectedProviderData.BusinessHours?.Days || []);
      setBusinessHoursStart(
        selectedProviderData.BusinessHours?.StartTime || ""
      );
      setBusinessHoursEnd(selectedProviderData.BusinessHours?.EndTime || "");
      setCacheEnabled(
        supportsBedrockPromptCaching(
          normalizeBedrockModelId(selectedProviderData.SelectedModal)
        ) &&
          (selectedProviderData.CacheEnabled === true ||
            selectedProviderData.CacheEnabled === "true")
      );
      setGreetingInterruptible(
        selectedProviderData.GreetingInterruptible === true ||
          selectedProviderData.GreetingInterruptible === "true"
      );
      setCacheTTL(selectedProviderData.CacheTTL != null ? Number(selectedProviderData.CacheTTL) : 300);
      setThinkingEnabled(
        selectedProviderData.ThinkingEnabled === true ||
          selectedProviderData.ThinkingEnabled === "true"
      );
      setReasoningEffort(selectedProviderData.ReasoningEffort || DEFAULT_REASONING_EFFORT);
      setEouMinDelay(selectedProviderData.EouMinDelay != null ? Number(selectedProviderData.EouMinDelay) : 0.3);
      setEouMaxDelay(selectedProviderData.EouMaxDelay != null ? Number(selectedProviderData.EouMaxDelay) : 0.8);
      setTurnDetectionMode(selectedProviderData.TurnDetectionMode === "semantic" ? "semantic" : "vad");
      setSttProvider(selectedProviderData.STTProvider === "Eleven Labs" ? "Eleven Labs" : "Deepgram");
      setElevenLabsTtsModel(selectedProviderData.ElevenLabsTTSModel || "eleven_flash_v2_5");
      const ivrMenuData = selectedProviderData.IvrMenu;
      if (ivrMenuData && ivrMenuData.enabled) {
        setIvrEnabled(true);
        setIvrOptions(ivrMenuData.options || []);
      } else {
        setIvrEnabled(false);
        setIvrOptions([]);
      }
      const agentConfigsData = selectedProviderData.AgentConfigs;
      if (agentConfigsData && agentConfigsData.length > 0) {
        const knownToolNames = apiFunctions.map((func) => func.Name);
        setMultiAgentEnabled(true);
        setAgentConfigs(
          agentConfigsData.map((cfg) => ({
            ...cfg,
            tools: Array.isArray(cfg.tools)
              ? cfg.tools.filter((tool) => knownToolNames.includes(tool))
              : cfg.tools,
          }))
        );
      } else {
        setMultiAgentEnabled(false);
        setAgentConfigs([]);
      }
      setBackgroundNoise(
        selectedProviderData.BackgroundNoise != null
          ? selectedProviderData.BackgroundNoise
          : NO_BACKGROUND_NOISE
      );
      setSipTerminationDynamicSeconds(
        selectedProviderData.SipTerminationDynamicSeconds != null
          ? String(selectedProviderData.SipTerminationDynamicSeconds)
          : DEFAULT_SIP_TERMINATION_SECONDS
      );
      setTransferSipTerminationDynamicSeconds(
        selectedProviderData.TransferSipTerminationDynamicSeconds != null
          ? String(selectedProviderData.TransferSipTerminationDynamicSeconds)
          : DEFAULT_TRANSFER_SIP_TERMINATION_SECONDS
      );
      setSelectedLanguages(
        selectedProviderData.SelectedLanguages &&
          selectedProviderData.SelectedLanguages.length > 0
          ? normalizeSelectedLanguages(selectedProviderData.SelectedLanguages)
          : DEFAULT_LANGUAGES
      );

      // Load KB data for this provider
      setKbUsagePrompt(selectedProviderData.KBUsagePrompt || "");
      fetchKbDocuments();

      resetInputFieldsColor();
    } else {
      sessionStorage.removeItem(PAUTHQ_PROVIDER_ID_STORAGE_KEY);
      resetForm();
    }

    // document.getElementById("submitButtonForm").removeAttribute("disabled");
  };

  const handleGetFile = async () => {
    let url = "/api/getS3File";
    let obj = {
      practiceId: PracticeId,
      providerId: providerId,
      keyType: "PromptFileUrl",
      memberListType: "InboundCallsOnboarding",
    };
    const userData = await dateRangeRequest(token, url, obj);
    if (userData == "Token is expired, login again") {
      setIsTokenExpired(true);
    } else if (userData == "No record") {
      setPromptFiledownloadLink("");
    } else {
      window.open(userData, "_blank");
    }
  };

  // Mirrors handleGetFile above, but for one row of the multi-agent table:
  // that prompt key lives inside the AgentConfigs JSON array, not a flat
  // column, so it needs its own endpoint keyed by agent index.
  const handleGetAgentPromptFile = async (index) => {
    let url = "/api/getAgentPromptFile";
    let obj = {
      practiceId: PracticeId,
      providerId: providerId,
      memberListType: "InboundCallsOnboarding",
      agentIndex: index,
    };
    const userData = await dateRangeRequest(token, url, obj);
    if (userData == "Token is expired, login again") {
      setIsTokenExpired(true);
    } else if (userData == "No record") {
      // Nothing to open; leave the current row display as-is.
    } else {
      window.open(userData, "_blank");
    }
  };

  const handlePromptFileChange = (e) => {
    enableElement("submitButtonForm");
    let file = e.target.files[0];
    if (file) {
      if (txtFormatChecker(file)) {
        setPromptFile(file);
      } else {
        setElementValue("dynamicPrompt", "");
        alert("Require format is not txt");
      }
    } else {
      setPromptFile("");
    }
  };

  const handleParameters = (index, field, value) => {
    const newParameters = [...parameters];
    newParameters[index][field] = value;
    setParameters(newParameters);
  };

  const addParameter = () => {
    setParameters([...parameters, { name: "", description: "" }]);
  };

  const removeParameter = (index) => {
    setParameters(parameters.filter((_, i) => i !== index));
  };

  const handleIvrOptionChange = (index, field, value) => {
    const updated = [...ivrOptions];
    updated[index][field] = value;
    setIvrOptions(updated);
  };

  const addIvrOption = () => {
    setIvrOptions([...ivrOptions, { digit: "", label: "", action: "agent", instruction: "" }]);
  };

  const removeIvrOption = (index) => {
    setIvrOptions(ivrOptions.filter((_, i) => i !== index));
  };

  const handleToggleIvr = (checked) => {
    setIvrEnabled(checked);
    if (!checked) setIvrOptions([]);
  };

  const handleToggleMultiAgent = (checked) => {
    setMultiAgentEnabled(checked);
    if (!checked) setAgentConfigs([]);
  };

  const addAgentConfig = () => {
    setAgentConfigs([...agentConfigs, { intent: "", promptUrl: "" }]);
  };

  const removeAgentConfig = (index) => {
    setAgentConfigs(agentConfigs.filter((_, i) => i !== index));
  };

  const handleAgentConfigChange = (index, field, value) => {
    const updated = [...agentConfigs];
    updated[index][field] = value;
    setAgentConfigs(updated);
  };

  // Empty selection gives the agent NO backend API tools (e.g. triage, which
  // only needs handoff tools); leaving this field untouched (key absent)
  // instead gives the agent every backend tool - see
  // agents/intent_agent.py filter_functions in the LiveKit repo.
  const handleAgentToolsChange = (index, tools) => {
    const updated = [...agentConfigs];
    updated[index].tools = tools;
    setAgentConfigs(updated);
  };

  const handleAgentPromptFileChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!txtFormatChecker(file)) {
      e.target.value = "";
      alert("Required format is not txt");
      return;
    }
    const updated = [...agentConfigs];
    updated[index].promptFile = file;
    updated[index].promptUrl = "";
    setAgentConfigs(updated);
  };

  // ── Knowledge Base handlers ──────────────────────────────────────────────

  const fetchKbDocuments = async () => {
    try {
      const data = await fetchUserRecord(
        token,
        `/api/knowledge-documents/${PracticeId}?memberListType=InboundCalls`
      );
      if (data && data.documents) {
        setKbDocuments(data.documents);
      } else {
        setKbDocuments([]);
      }
    } catch {
      setKbDocuments([]);
    }
  };

  const handleKbFileChange = (e) => {
    setKbFile(e.target.files[0] || null);
    setKbError("");
  };

  const handleKbUpload = async () => {
    if (!kbFile) {
      setKbError("Please select a file to upload.");
      return;
    }
    setKbUploading(true);
    setKbError("");
    try {
      const form = new FormData();
      form.append("file", kbFile);
      form.append("practiceId", PracticeId);
      form.append("memberListType", "InboundCalls");
      if (providerId) form.append("providerId", providerId);

      const resp = await fetch("/api/knowledge-documents/upload", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await resp.json();
      if (!resp.ok) {
        setKbError(data.error || "Upload failed.");
      } else {
        setKbFile(null);
        setElementValue("kbFileInput", "");
        await fetchKbDocuments(PracticeId);
      }
    } catch (err) {
      setKbError("Upload failed. Please try again.");
    } finally {
      setKbUploading(false);
    }
  };

  const handleKbDelete = async (docId) => {
    if (!window.confirm("Remove this document from the knowledge base?")) return;
    try {
      const resp = await fetch(`/api/knowledge-documents/${docId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        await fetchKbDocuments(PracticeId);
      } else {
        const data = await resp.json();
        setKbError(data.error || "Delete failed.");
      }
    } catch {
      setKbError("Delete failed. Please try again.");
    }
  };

  const handleKbUsagePromptSave = async () => {
    setKbSavingPrompt(true);
    setKbError("");
    try {
      const resp = await fetch("/api/knowledge-base/usage-prompt", {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          practiceId: PracticeId,
          providerId: providerId || undefined,
          memberListType: "InboundCalls",
          kbUsagePrompt,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setKbError(data.error || "Failed to save KB usage prompt.");
      }
    } catch {
      setKbError("Failed to save KB usage prompt.");
    } finally {
      setKbSavingPrompt(false);
    }
  };

  const handleSubmit = async (e) => {
    setLoading(true);
    e.target.disabled = true;

    document.querySelectorAll(".px-3").forEach((e) => {
      const selected_Languages = document.querySelector(
        ".autocomplete_languages"
      );

      if (e.value === "" && e.id !== "selected_bgNoise") {
        e.classList.add("invalid-details");
      }

      if (selectedLanguages.length === 0 && selected_Languages) {
        selected_Languages.classList.add("invalid-details");
      }
    });

    const parametersFilled = parameters.every(
      (param) => param.name.trim() !== "" && param.description.trim() !== ""
    );
    const hasMissingRequiredFields =
      providerName === "" ||
      providerFacilityName === "" ||
      providerContactNumber === "" ||
      firstMessage === "" ||
      lastMessage === "" ||
      status === "" ||
      !parametersFilled ||
      selectedModal === "" ||
      selectedVoiceModal === "" ||
      selectedVoice === "" ||
      selectedTimezone === "" ||
      selectedLanguages.length === 0 ||
      backgroundNoise === BACKGROUND_NOISE_PLACEHOLDER;
    isAnyUploadFieldEmpty = hasMissingRequiredFields;

    if (
      providerContactNumber !== ""
    ) {
      const isValid = isValidContactNumber(providerContactNumber);
      if (isValid === false) {
        addElementClass("ProviderContactNumber", "invalid-details");
        isValidDetails = true;
      }
    }

    if (
      providerContactNumber !== "" &&
      isValidContactNumber(providerContactNumber) === true
    ) {
      isValidDetails = false;
    }

    if (isAnyUploadFieldEmpty === true) {
      alert("Please enter complete details of onboarding");
      enableElement("submitButtonForm");
      setLoading(false);
      return;
    }

    if (isValidDetails === true) {
      enableElement("submitButtonForm");
      setLoading(false);
      return;
    }

    formData.append("PromptFile", promptFile);
    if (promptFile !== "") {
      formData.append("PromptFileUrl", "");
    } else {
      formData.append("PromptFileUrl", promptFiledownloadLink);
    }
    formData.append("PracticeId", PracticeId);
    formData.append("ProviderId", providerId);
    formData.append("ProviderName", providerName);
    formData.append("ProviderFacilityName", providerFacilityName);
    formData.append("NationalProviderIdentifier", "");
    formData.append("ProviderContactNumber", providerContactNumber);
    formData.append("phoneNumberSid", selectedNumberSid);
    formData.append("ProviderFaxNumber", "");
    formData.append("ProviderEmailAddress", "");
    formData.append("SelectedModal", normalizeBedrockModelId(selectedModal));
    formData.append("SelectedVoice", selectedVoice);
    formData.append("SelectedVoiceModal", selectedVoiceModal);
    formData.append("STTModel", selectedSttModel);
    formData.append("FluxKeyterms", isFluxKeytermEnabled ? fluxKeyterms : "");
    formData.append("FirstMessage", firstMessage);
    formData.append("LastMessage", lastMessage);
    formData.append("Status", status);
    formData.append("Parameters", JSON.stringify(parameters));
    formData.append("API_Keys", "");
    formData.append("Timezone", selectedTimezone);
    formData.append(
      "BusinessHours",
      businessHoursDays.length > 0 && businessHoursStart && businessHoursEnd
        ? JSON.stringify({
            Days: businessHoursDays,
            StartTime: businessHoursStart,
            EndTime: businessHoursEnd,
          })
        : ""
    );
    formData.append(
      "CacheEnabled",
      isBedrockCacheAvailable ? cacheEnabled : false
    );
    formData.append("GreetingInterruptible", greetingInterruptible);
    formData.append("CacheTTL", isBedrockCacheAvailable && cacheEnabled ? cacheTTL : 300);
    const reasoning = normalizeReasoningSelection(selectedModal, thinkingEnabled, reasoningEffort);
    formData.append("ThinkingEnabled", reasoning.thinkingEnabled);
    formData.append("ReasoningEffort", reasoning.reasoningEffort);
    formData.append("EouMinDelay", eouMinDelay);
    formData.append("EouMaxDelay", eouMaxDelay);
    formData.append("TurnDetectionMode", turnDetectionMode);
    formData.append("STTProvider", sttProvider);
    formData.append("ElevenLabsTTSModel", elevenLabsTtsModel);
    formData.append("IvrMenu", JSON.stringify({ enabled: ivrEnabled, options: ivrOptions }));
    formData.append(
      "BackgroundNoise",
      backgroundNoise === BACKGROUND_NOISE_PLACEHOLDER
        ? NO_BACKGROUND_NOISE
        : backgroundNoise
    );
    if (multiAgentEnabled && agentConfigs.length > 0) {
      // Spread keeps extra keys set outside this form; promptFile (local File
      // object) is UI-only and must not be persisted. tools is ALWAYS
      // emitted: an untouched blank field must save [] (no backend tools, as
      // the column label says) - an absent key means "all backend tools" to
      // the agent.
      const configsMeta = agentConfigs.map(({ promptFile, ...cfg }) => ({
        ...cfg,
        promptUrl: cfg.promptUrl || "",
        tools: Array.isArray(cfg.tools) ? cfg.tools : [],
      }));
      formData.append("AgentConfigs", JSON.stringify(configsMeta));
      agentConfigs.forEach((cfg, i) => {
        if (cfg.promptFile) formData.append(`AgentPromptFile_${i}`, cfg.promptFile);
      });
    } else {
      formData.append("AgentConfigs", "");
    }
    formData.append("SipTerminationDynamicSeconds", sipTerminationDynamicSeconds !== "" ? Number(sipTerminationDynamicSeconds) : "");
    formData.append("TransferSipTerminationDynamicSeconds", transferSipTerminationDynamicSeconds !== "" ? Number(transferSipTerminationDynamicSeconds) : "");
    formData.append("SelectedLanguages", selectedLanguages);
    // let obj = {
    //     PracticeId: PracticeId,
    //     ProviderName: ProviderName,
    //     ProviderFacilityName: ProviderFacilityName,
    //     NationalProviderIdentifier: NationalProviderIdentifier,
    //     ProviderContactNumber: ProviderContactNumber,
    //     ProviderFaxNumber: ProviderFaxNumber,
    //     ProviderEmailAddress: ProviderEmailAddress,
    // }

    let url = "/api/PAuthQOnBoardingTemplate";
    const userData = await usersPostRequest(token, url, formData);
    if (userData == "Token is expired, login again") {
      setIsTokenExpired(true);
    } else if (userData && userData.message) {
      setLoading(false);
      alert(userData.message);
      fetchApiHandle();
    }

    setProviderName("");
    setProviderContactNumber("");
    // setNationalProviderIdentifier("");
    // setProviderFaxNumber("");
    // setApiKeys("");
    setProviderId("");
    sessionStorage.removeItem(PAUTHQ_PROVIDER_ID_STORAGE_KEY);
    setSelectedNumberSid("");
    setFluxKeyterms("");
    setFirstMessage("");
    setLastMessage("");
    setStatus(DEFAULT_STATUS);
    setParameters([]);
    setSelectedTimezone("");
    setBusinessHoursDays([]);
    setBusinessHoursStart("");
    setBusinessHoursEnd("");
    setCacheEnabled(false);
    setGreetingInterruptible(false);
    setCacheTTL(300);
    setThinkingEnabled(false);
    setReasoningEffort(DEFAULT_REASONING_EFFORT);
    setEouMinDelay(0.3);
    setEouMaxDelay(0.8);
    setTurnDetectionMode("vad");
    setSttProvider("Deepgram");
    setElevenLabsTtsModel("eleven_flash_v2_5");
    setIvrEnabled(false);
    setIvrOptions([]);
    setMultiAgentEnabled(false);
    setAgentConfigs([]);
    setBackgroundNoise("");
    setSipTerminationDynamicSeconds("");
    setTransferSipTerminationDynamicSeconds("");
    setSelectedLanguages([]);
    setBackgroundNoise(DEFAULT_BACKGROUND_NOISE);
    setSipTerminationDynamicSeconds(DEFAULT_SIP_TERMINATION_SECONDS);
    setTransferSipTerminationDynamicSeconds(DEFAULT_TRANSFER_SIP_TERMINATION_SECONDS);
    setSelectedLanguages(DEFAULT_LANGUAGES);
    setElementValue("selected_Provider", "");

    // For admin
    if (userType === "admin" || userType === "client") {
      setPromptFile("");
      setPromptFiledownloadLink("");
      setSelectedModal(DEFAULT_MODAL);
      setSelectedVoice(DEFAULT_VOICE_PROVIDER);
      setSelectedVoiceModal(getDefaultVoiceModalValue(DEFAULT_VOICE_PROVIDER));
      setElementValue("dynamicPrompt", "");
      setElementValue("selected_modal", DEFAULT_MODAL);
      setElementValue("selected_voice", DEFAULT_VOICE_PROVIDER);
      setElementValue(
        "selected_voiceModal",
        getDefaultVoiceModalValue(DEFAULT_VOICE_PROVIDER)
      );
      setElementValue("selected_timezone", "");
      setElementValue("selected_bgNoise", DEFAULT_BACKGROUND_NOISE);
    }
  };

  const handleProviderContact = (e) => {
    if (isNumber.test(e.target.value)) {
      setProviderContactNumber(e.target.value);
    }
  };

  const handleInputValues = (e) => {
    if (e.target.value !== "" || e.target.value === "") {
      enableElement("submitButtonForm");
    }
  };

  useEffect(() => {
    if (selectedVoice) {
      filterVoiceModals();
      const matchingVoiceModalValues = voiceModals
        .filter((voice) => voice.CategoryLabel === getVoiceCategory(selectedVoice))
        .map((voice) => voice.SubCategoryValue);
      const defaultVoiceModal = getDefaultVoiceModalValue(selectedVoice);

      if (
        defaultVoiceModal &&
        (!selectedVoiceModal ||
          !matchingVoiceModalValues.includes(selectedVoiceModal))
      ) {
        setSelectedVoiceModal(defaultVoiceModal);
      }
    } else {
      setSelectedVoiceModals([]);
    }
  }, [selectedVoice, selectedVoiceModal, voiceModals]);

  // Token-expiry redirect is now handled once, centrally, in ContextApi.js - see its comment
  // for why this component's own copy of the same effect was removed.

  useEffect(() => {
    if (PracticeId || activeTab) {
      fetchApiHandle();
      fetchVoiceAndModals();
      resetForm();
      setProviderFacilityName(practiceName);
    }
  }, [activeTab, PracticeId]);

  // After a refresh, once the provider list has loaded, re-select whichever
  // provider was being edited before instead of leaving the form on blank.
  useEffect(() => {
    if (restoredProviderRef.current) return;
    if (!providerData || providerData.length === 0) return;

    const persistedProviderId = sessionStorage.getItem(PAUTHQ_PROVIDER_ID_STORAGE_KEY);
    if (persistedProviderId) {
      const match = providerData.find(
        (provider) => String(provider.Id) === persistedProviderId
      );
      if (match) {
        updateForm({ target: { value: String(match.Id) } });
        setElementValue("selected_Provider", String(match.Id));
      } else {
        sessionStorage.removeItem(PAUTHQ_PROVIDER_ID_STORAGE_KEY);
      }
    }
    restoredProviderRef.current = true;
  }, [providerData]);

  useEffect(() => {
    if (
      providerName != "" ||
      providerFacilityName != "" ||
      (inboundContact ? inboundContact : "") !== "" ||
      firstMessage !== "" ||
      lastMessage !== "" ||
      status !== "" ||
      promptFile != "" ||
      selectedModal !== "" ||
      selectedVoice !== "" ||
      selectedVoiceModal !== "" ||
      selectedTimezone !== "" ||
      backgroundNoise !== DEFAULT_BACKGROUND_NOISE ||
      selectedLanguages.length !== 0 ||
      businessHoursDays.length !== 0
    ) {
      enableElement("submitButtonForm");
    } else {
      disableElement("submitButtonForm");
    }
  }, [
    providerName,
    providerFacilityName,
    inboundContact,
    firstMessage,
    lastMessage,
    status,
    promptFile,
    selectedModal,
    selectedVoice,
    selectedVoiceModal,
    selectedTimezone,
    selectedLanguages,
    backgroundNoise,
    businessHoursDays,
  ]);

  const nextStep = () => {
    setCurrentStep((step) => Math.min(step + 1, INBOUND_PROMPT_STEPS.length));
  };

  const previousStep = () => {
    setCurrentStep((step) => Math.max(step - 1, 1));
  };

  return (
    <>
      <div className="container py-4 px-4 onboardingLayout shadow-sm">
        <div className="row p-3 col-12 col-xl-10 m-auto login-container onboarding-forms bg_dark_opacity prompt-hub-panel">
          <div className="mb-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
            <h6 className="fw-bold mt-2 text-white mb-1">Inbound Prompt Hub</h6>
            <button
              type="button"
              className="simple-btn register-back-btn auth-register-actions__button--secondary"
              onClick={() => navigate("/inbound")}
            >
              Back to Console
            </button>
          </div>

          <div className="auth-stepper mb-4" aria-label="Inbound prompt hub steps">
            {INBOUND_PROMPT_STEPS.map((step) => {
              const isActive = step.id === currentStep;
              const isCompleted = step.id < currentStep;

              return (
                <button
                  key={step.id}
                  type="button"
                  className={`auth-stepper__item${isActive ? " is-active" : ""}${isCompleted ? " is-complete" : ""}`}
                  onClick={() => setCurrentStep(step.id)}
                >
                  <span className="auth-stepper__dot">{step.id}</span>
                  <span className="auth-stepper__copy">
                    <span className="auth-stepper__label">{step.label}</span>
                    <span className="auth-stepper__title">{step.title}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="col auth-register-flow prompt-hub-flow">
            {currentStep === 1 ? (
              <section className="auth-form-section auth-step-panel">
                <div className="auth-form-section__header auth-step-panel__header">
                  <div>
                    <p className="auth-step-panel__eyebrow">Step 1</p>
                    <h3 className="auth-form-section__title">Basic Details</h3>
                  </div>
                </div>
            <div className="mb-3">
              <label className="text-white form-label">
                Organization Name
              </label>
              <input
                type="text"
                onChange={(e) => setProviderFacilityName(e.target.value)}
                value={providerFacilityName}
                disabled
                onFocus={resetErrorFieldStyle}
                className="bg-black text-white border-0 py-2 px-3 form-control"
                id="ProviderFacilityName"
                placeholder="Enter your clinic name"
              />
            </div>
            <div className="mb-3">
              <label className="text-white form-label">
                Select Assistant to edit
              </label>
              <select
                className="bg-black border-0 text-white padding_left py-2 form-select"
                id="selected_Provider"
                onChange={updateForm}
              >
                <option className="selectedProviderOption" value="">
                  Select Assistant
                </option>
                {providerData.length > 0
                  ? providerData.map((provider) => {
                    return (
                      <option
                        key={provider.Id}
                        className="selectedProviderOption"
                        value={provider.Id}
                      >
                        {provider.ProviderName}
                      </option>
                    );
                  })
                  : ""}
              </select>
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Assistant Name</label>
              <input
                type="text"
                onChange={(e) => setProviderName(e.target.value)}
                value={providerName}
                className="bg-black border-0 text-white py-2 px-3 form-control"
                onFocus={resetErrorFieldStyle}
                id="ProviderName"
                placeholder="Enter your clinic name"
              />
            </div>
            {/* <div className="mb-3">
                            <label className="text-white form-label">National Provider Identifier (NPI)</label>
                            <input type="text" onChange={(e) => setNationalProviderIdentifier(e.target.value)} value={nationalProviderIdentifier} className="bg-black text-white border-0 py-2 px-3 form-control" onFocus={resetErrorFieldStyle} id="NationalProviderIdentifier" placeholder="Enter your clinic name" />
                        </div> */}
            <div className="mb-3">
              <label className="text-white form-label">Inbound Trunk Number</label>
              <Autocomplete
                id="ProviderContactNumber"
                options={[
                  ...availableNumbers,
                  ...(legacyNumberOption &&
                    !availableNumbers.find(
                      (av) => av.phoneNumber === legacyNumberOption.phoneNumber
                    )
                    ? [legacyNumberOption]
                    : []),
                ]}
                getOptionLabel={(option) => option.phoneNumber || ""}
                value={legacyNumberOption}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setProviderContactNumber(newValue.phoneNumber);
                    setSelectedNumberSid(newValue.sid || "");
                  } else {
                    setProviderContactNumber("");
                    setSelectedNumberSid("");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select a number"
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        minHeight: "46px",
                        color: "#280137",
                        backgroundColor: "#ffffff",
                        borderRadius: "14px",
                        "& fieldset": { borderColor: "rgba(65, 60, 88, 0.12)" },
                        "&:hover fieldset": {
                          borderColor: "rgba(99, 201, 220, 0.32)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "rgba(99, 201, 220, 0.42)",
                        },
                      },
                      "& .MuiAutocomplete-input": {
                        color: "#280137 !important",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "rgba(109, 95, 131, 0.76)",
                        opacity: 1,
                      },
                      "& .MuiSvgIcon-root": {
                        color: "#6d5f83",
                      },
                    }}
                  />
                )}
                className="prompt-hub-autocomplete"
              />
            </div>
            {/* <div className="mb-3">
                            <label className="text-white form-label">Fax Number</label>
                            <input type="text" onChange={(e) => setProviderFaxNumber(e.target.value)} value={providerFaxNumber} className="bg-black text-white border-0 py-2 px-3 form-control" onFocus={resetErrorFieldStyle} id="ProviderFaxNumber" placeholder="Add a row" />
                        </div> */}
            {/* {
                            userType === "admin" ?
                                <> */}
            <div className="mb-3">
              <label className="text-white form-label">
                Upload Dynamic Prompt File
              </label>
              <input
                type="file"
                onChange={handlePromptFileChange}
                accept={".txt"}
                className="bg-black text-white border-0 py-2 padding_left form-control"
                onFocus={resetErrorFieldStyle}
                id="dynamicPrompt"
              />
              {promptFiledownloadLink &&
                promptFiledownloadLink.startsWith("http") ? (
                <a
                  href={promptFiledownloadLink}
                  target="blank"
                  download={promptFiledownloadLink}
                  className="prompt-hub-download-link"
                >
                  View File
                </a>
              ) : (
                promptFiledownloadLink && (
                  <Link
                    className="prompt-hub-download-link"
                    onClick={() => handleGetFile("prompt")}
                  >
                    View File
                  </Link>
                )
              )}
            </div>
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={multiAgentEnabled}
                  onChange={(e) => handleToggleMultiAgent(e.target.checked)}
                  id="multiAgentEnabled"
                />
                <label className="form-check-label text-white" htmlFor="multiAgentEnabled">
                  Enable Multi-Agent System
                </label>
              </div>
              {multiAgentEnabled && (
                <div className="mt-3" style={{ overflowX: "auto", maxWidth: "100%" }}>
                  <table className="agent-config-table">
                    <thead>
                      <tr>
                        <th>Intent</th>
                        <th>Prompt File</th>
                        <th>Tools (blank = none)</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {agentConfigs.map((cfg, i) => (
                        <tr key={i}>
                          <td>
                            <input
                              type="text"
                              value={cfg.intent}
                              onChange={(e) => handleAgentConfigChange(i, "intent", e.target.value)}
                              className="form-control"
                              placeholder="e.g. triage"
                            />
                          </td>
                          <td>
                            <input
                              type="file"
                              accept=".txt"
                              onChange={(e) => handleAgentPromptFileChange(i, e)}
                              className="form-control"
                            />
                            {cfg.promptUrl && (
                              cfg.promptUrl.startsWith("http") ? (
                                <a
                                  href={cfg.promptUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="prompt-hub-download-link"
                                >
                                  View File
                                </a>
                              ) : (
                                <Link
                                  className="prompt-hub-download-link"
                                  onClick={() => handleGetAgentPromptFile(i)}
                                >
                                  View File
                                </Link>
                              )
                            )}
                          </td>
                          <td>
                            <ToolsMultiSelectDropdown
                              id={`agent-tools-${i}`}
                              value={cfg.tools || []}
                              onChange={(tools) => handleAgentToolsChange(i, tools)}
                              options={apiFunctions.map((func) => func.Name)}
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              onClick={() => removeAgentConfig(i)}
                              className="btn btn-sm btn-danger agent-config-table__remove-btn"
                              aria-label="Remove agent"
                              title="Remove agent"
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button
                    type="button"
                    onClick={addAgentConfig}
                    className="btn btn-sm btn-outline-light mt-2"
                  >
                    + Add Agent
                  </button>
                </div>
              )}
            </div>
            <TimeZoneDropdown
              selectedTimezone={selectedTimezone}
              onChange={handleTimezoneChange}
            />
            <BusinessHoursPicker
              days={businessHoursDays}
              onDaysChange={setBusinessHoursDays}
              startTime={businessHoursStart}
              onStartTimeChange={setBusinessHoursStart}
              endTime={businessHoursEnd}
              onEndTimeChange={setBusinessHoursEnd}
            />
              </section>
            ) : null}

            {currentStep === 2 ? (
              <section className="auth-form-section auth-step-panel">
                <div className="auth-form-section__header auth-step-panel__header">
                  <div>
                    <p className="auth-step-panel__eyebrow">Step 2</p>
                    <h3 className="auth-form-section__title">Configuration</h3>
                  </div>
                </div>
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={ivrEnabled}
                  onChange={(e) => handleToggleIvr(e.target.checked)}
                  id="ivrEnabled"
                />
                <label className="form-check-label text-white" htmlFor="ivrEnabled">
                  Enable IVR Menu
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={greetingInterruptible}
                  onChange={(e) => setGreetingInterruptible(e.target.checked)}
                  id="greetingInterruptible"
                />
                <label className="form-check-label text-white" htmlFor="greetingInterruptible">
                  Allow callers to interrupt the opening greeting
                </label>
              </div>
              {ivrEnabled && (
                <div className="mt-3">
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={{ color: "#fff", padding: "6px 8px", textAlign: "left", fontSize: "13px" }}>Digit</th>
                        <th style={{ color: "#fff", padding: "6px 8px", textAlign: "left", fontSize: "13px" }}>Label</th>
                        <th style={{ color: "#fff", padding: "6px 8px", textAlign: "left", fontSize: "13px" }}>Action</th>
                        <th style={{ color: "#fff", padding: "6px 8px", textAlign: "left", fontSize: "13px" }}>What should agent do</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {ivrOptions.map((opt, i) => (
                        <tr key={i}>
                          <td style={{ padding: "4px 8px" }}>
                            <input
                              type="text"
                              maxLength={1}
                              value={opt.digit}
                              onChange={(e) => handleIvrOptionChange(i, "digit", e.target.value)}
                              className="bg-black border-0 text-white py-1 px-2 form-control"
                              style={{ width: "55px" }}
                              placeholder="1"
                            />
                          </td>
                          <td style={{ padding: "4px 8px" }}>
                            <input
                              type="text"
                              value={opt.label}
                              onChange={(e) => handleIvrOptionChange(i, "label", e.target.value)}
                              className="bg-black border-0 text-white py-1 px-2 form-control"
                              style={{ width: "180px" }}
                              placeholder="e.g. Billing questions"
                            />
                          </td>
                          <td style={{ padding: "4px 8px" }}>
                            <select
                              value={opt.action}
                              onChange={(e) => handleIvrOptionChange(i, "action", e.target.value)}
                              className="bg-black border-0 text-white py-1 px-2 form-select"
                              style={{ width: "160px" }}
                            >
                              <option value="agent">Agent handles</option>
                              <option value="transfer">Transfer</option>
                            </select>
                          </td>
                          <td style={{ padding: "4px 8px" }}>
                            <input
                              type="text"
                              value={opt.instruction || ""}
                              onChange={(e) => handleIvrOptionChange(i, "instruction", e.target.value)}
                              className="bg-black border-0 text-white py-1 px-2 form-control"
                              style={{ width: "220px" }}
                              placeholder="e.g. Ask the caller for their name and DOB"
                            />
                          </td>
                          <td style={{ padding: "4px 8px" }}>
                            <button
                              type="button"
                              onClick={() => removeIvrOption(i)}
                              className="btn btn-sm btn-danger"
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button
                    type="button"
                    onClick={addIvrOption}
                    className="btn btn-sm btn-outline-light mt-2"
                  >
                    + Add Option
                  </button>
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="text-white form-label">
                Select Background Noise
              </label>
              <select
                className="bg-black border-0 text-white py-2 px-3 form-select"
                id="selected_bgNoise"
                value={backgroundNoise}
                onChange={(e) => setBackgroundNoise(e.target.value)}
                onFocus={resetErrorFieldStyle}
              >
                <option
                  className="selectedProviderOption"
                  value={BACKGROUND_NOISE_PLACEHOLDER}
                  disabled
                >
                  Select background noise
                </option>
                <option className="selectedProviderOption" value="">
                  None
                </option>
                <option className="selectedProviderOption" value="keyboard">
                  Keyboard
                </option>
              </select>
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Sip Termination Dynamic Seconds</label>
              <input
                type="number"
                className="bg-black border-0 text-white py-2 px-3 form-control"
                id="sip_termination_dynamic_seconds"
                value={sipTerminationDynamicSeconds}
                onChange={(e) => setSipTerminationDynamicSeconds(e.target.value)}
                placeholder="e.g. 900"
                min="1"
              />
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Transfer Sip Termination Dynamic Seconds</label>
              <input
                type="number"
                className="bg-black border-0 text-white py-2 px-3 form-control"
                id="transfer_sip_termination_dynamic_seconds"
                value={transferSipTerminationDynamicSeconds}
                onChange={(e) => setTransferSipTerminationDynamicSeconds(e.target.value)}
                placeholder="e.g. 900"
                min="1"
                title="How long a call is allowed to run AFTER a transfer connects before it's automatically disconnected - separate from the Sip Termination Dynamic Seconds cap above."
              />
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Select Languages</label>
              <div>
                <MultiSelectDropdown
                  id="selected_languages"
                  value={selectedLanguages}
                  onChange={setSelectedLanguages}
                  options={optionsForLanguages}
                  placeholder="Select languages ..."
                  className="autocomplete_languages"
                  onFocus={resetErrorFieldStyle}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="text-white form-label">First Message</label>
              <input
                type="text"
                onChange={(e) => setFirstMessage(e.target.value)}
                value={firstMessage}
                onFocus={resetErrorFieldStyle}
                className="bg-black text-white border-0 py-2 px-3 form-control"
                id="firstMessage"
                placeholder="First Message"
              />
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Last Message</label>
              <input
                type="text"
                onChange={(e) => setLastMessage(e.target.value)}
                value={lastMessage}
                onFocus={resetErrorFieldStyle}
                className="bg-black text-white border-0 py-2 px-3 form-control"
                id="lastMessage"
                placeholder="Last Message"
              />
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Status</label>
              <input
                type="text"
                onChange={(e) => setStatus(e.target.value)}
                value={status}
                onFocus={resetErrorFieldStyle}
                className="bg-black text-white border-0 py-2 px-3 form-control"
                id="status"
                placeholder="Status"
              />
            </div>
            <div className="mb-3 text-white">
              <label className="text-white form-label">
                Summarize Parameters
              </label>

              {parameters.map((param, index) => (
                <div key={index} className="mb-2">
                  <div className="d-flex align-items-center mb-2">
                    <input
                      type="text"
                      onChange={(e) =>
                        handleParameters(index, "name", e.target.value)
                      }
                      value={param.name}
                      onFocus={resetErrorFieldStyle}
                      className="bg-black text-white border-0 px-3 py-2 form-control"
                      placeholder="Name"
                      style={{
                        paddingLeft: "1rem",
                      }}
                      id={`name${index}`}
                    />
                    <button
                      onClick={() => removeParameter(index)}
                      className="delete-btn"
                    >
                      ✕
                    </button>
                  </div>

                  <input
                    type="text"
                    onChange={(e) =>
                      handleParameters(index, "description", e.target.value)
                    }
                    value={param.description}
                    onFocus={resetErrorFieldStyle}
                    className="bg-black text-white border-0 py-2 px-3 form-control"
                    placeholder="Description"
                    id={`description${index}`}
                  />
                </div>
              ))}
              <div className="mb-3">
                <button onClick={addParameter} className="simple-btn login-btn">
                  Add Parameters +
                </button>
              </div>
            </div>
              </section>
            ) : null}

            {currentStep === 3 ? (
              <section className="auth-form-section auth-step-panel">
                <div className="auth-form-section__header auth-step-panel__header">
                  <div>
                    <p className="auth-step-panel__eyebrow">Step 3</p>
                    <h3 className="auth-form-section__title">Model & Publish</h3>
                  </div>
                </div>
            {/* <div className="mb-3">
                                        <label className="text-white form-label">API Keys</label>
                                        <input type="text" onChange={(e) => setApiKeys(e.target.value)} value={apiKeys} onFocus={resetErrorFieldStyle} className="bg-black text-white border-0 py-2 px-3 form-control" id="apiKeys" placeholder="API Keys" />
                                    </div> */}
            <div className="mb-3">
              <label className="text-white form-label">Select Modal</label>
              <select
                className="bg-black border-0 text-white py-2 px-3 form-select"
                id="selected_modal"
                value={selectedModal}
                onChange={(e) => setSelectedModal(e.target.value)}
                onFocus={resetErrorFieldStyle}
              >
                <option className="selectedProviderOption" value="">
                  Select Modal
                </option>

                <option
                  className="selectedProviderOption"
                  value="us.anthropic.claude-3-7-sonnet-20250219-v1:0"
                >
                  Anthropic Claude 3.7 Sonnet
                </option>
                <option
                  className="selectedProviderOption"
                  value="us.anthropic.claude-haiku-4-5-20251001-v1:0"
                >
                  Anthropic Claude Haiku 4.5
                </option>
                <option
                  className="selectedProviderOption"
                  value="us.anthropic.claude-sonnet-4-6"
                >
                  Anthropic Claude Sonnet 4.6
                </option>
                <option
                  className="selectedProviderOption"
                  value="us.anthropic.claude-sonnet-5"
                >
                  Anthropic Claude Sonnet 5
                </option>
                <option
                  className="selectedProviderOption"
                  value="us.anthropic.claude-sonnet-5-5"
                >
                  Anthropic Claude Sonnet 5.5
                </option>
                <option
                  className="selectedProviderOption"
                  value="us.anthropic.claude-haiku-5-5"
                >
                  Anthropic Claude Haiku 5.5
                </option>
                <option
                  className="selectedProviderOption"
                  value="us.xai.grok-4.7"
                >
                  xAI Grok 4.7
                </option>
                <option
                  className="selectedProviderOption"
                  value="us.amazon.nova-lite-v1:0"
                >
                  Amazon Nova Lite
                </option>
                <option
                  className="selectedProviderOption"
                  value="us.amazon.nova-pro-v1:0"
                >
                  Amazon Nova Pro
                </option>
                <option
                  className="selectedProviderOption"
                  value="groq/meta-llama/llama-4-maverick-17b-128e-instruct"
                >
                  Allyzent 70B
                </option>
                <option
                  className="selectedProviderOption"
                  value="openai/gpt-4.1-mini"
                >
                  G-4.1 mini
                </option>
                <option
                  className="selectedProviderOption"
                  value="openai/gpt-4o-mini"
                >
                  G-4o mini
                </option>
                <option
                  className="selectedProviderOption"
                  value="bedrock-mantle/openai.gpt-5.6-terra"
                >
                  GPT-5.6 Terra
                </option>
                <option
                  className="selectedProviderOption"
                  value="bedrock-mantle/openai.gpt-5.6-luna"
                >
                  GPT-5.6 Luna
                </option>
              </select>
            </div>
            <LlmReasoningControls
              idPrefix="inbound"
              model={selectedModal}
              thinkingEnabled={thinkingEnabled}
              onThinkingChange={setThinkingEnabled}
              reasoningEffort={reasoningEffort}
              onEffortChange={setReasoningEffort}
            />
            <div className="mb-3">
              {isBedrockCacheAvailable ? (
                <>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={cacheEnabled}
                      onChange={(e) => setCacheEnabled(e.target.checked)}
                      id="cacheEnabled"
                    />
                    <label
                      className="form-check-label text-white"
                      htmlFor="cacheEnabled"
                    >
                      Enable Bedrock prompt caching
                    </label>
                  </div>
                  {cacheEnabled && (
                    <div className="mt-2">
                      <label className="text-white-50 d-block mb-1" style={{ fontSize: "0.8rem" }}>
                        Cache TTL
                      </label>
                      <div className="d-flex gap-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="cacheTTL5m"
                            name="cacheTTL"
                            value={300}
                            checked={cacheTTL === 300}
                            onChange={() => setCacheTTL(300)}
                          />
                          <label className="form-check-label text-white-50" htmlFor="cacheTTL5m" style={{ fontSize: "0.8rem" }}>
                            5 minutes (default)
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="cacheTTL1h"
                            name="cacheTTL"
                            value={3600}
                            checked={cacheTTL === 3600}
                            onChange={() => setCacheTTL(3600)}
                          />
                          <label className="form-check-label text-white-50" htmlFor="cacheTTL1h" style={{ fontSize: "0.8rem" }}>
                            1 hour (extended)
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                  <p
                    className="text-white-50 mb-0 mt-2"
                    style={{ fontSize: "0.8rem", lineHeight: 1.6 }}
                  >
                    Available for Claude Haiku 4.5 / 5.5 and Claude Sonnet
                    4.6 / 5 / 5.5 only. This caches repeated system prompt
                    and tool-definition tokens.
                  </p>
                </>
              ) : (
                <p
                  className="text-white-50 mb-0"
                  style={{ fontSize: "0.8rem", lineHeight: 1.6 }}
                >
                  {selectedModal.includes("grok")
                    ? "Grok 4.7 caches repeated prompt tokens automatically; there is nothing to configure."
                    : "Bedrock prompt caching becomes available when you select Claude Haiku 4.5 / 5.5 or Claude Sonnet 4.6 / 5 / 5.5."}
                </p>
              )}
            </div>
            <div className="mb-3">
              <label className="text-white form-label">End-of-Utterance Delays</label>
              <div className="d-flex gap-3">
                <div className="flex-fill">
                  <label className="text-white-50 d-block mb-1" style={{ fontSize: "0.8rem" }}>
                    Min delay (seconds)
                  </label>
                  <input
                    type="number"
                    className="bg-black border-0 text-white py-2 px-3 form-control"
                    min="0.1"
                    max="2.0"
                    step="0.1"
                    value={eouMinDelay}
                    onChange={(e) => setEouMinDelay(Number(e.target.value))}
                  />
                </div>
                <div className="flex-fill">
                  <label className="text-white-50 d-block mb-1" style={{ fontSize: "0.8rem" }}>
                    Max delay (seconds)
                  </label>
                  <input
                    type="number"
                    className="bg-black border-0 text-white py-2 px-3 form-control"
                    min="1.0"
                    max="8.0"
                    step="0.1"
                    value={eouMaxDelay}
                    onChange={(e) => setEouMaxDelay(Number(e.target.value))}
                  />
                </div>
              </div>
              <p className="text-white-50 mb-0 mt-2" style={{ fontSize: "0.8rem", lineHeight: 1.6 }}>
                Min: how long to wait after silence before treating it as end-of-turn.
                Max: hard ceiling before the agent always responds.
              </p>
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Turn Detection</label>
              <select
                className="bg-black border-0 text-white py-2 px-3 form-select"
                id="turn_detection_mode"
                value={turnDetectionMode}
                onChange={(e) => setTurnDetectionMode(e.target.value)}
                onFocus={resetErrorFieldStyle}
              >
                <option className="selectedProviderOption" value="vad">
                  Voice activity only (default)
                </option>
                <option className="selectedProviderOption" value="semantic">
                  Semantic model (English/multilingual auto-selected by language)
                </option>
              </select>
              <p className="text-white-50 mb-0 mt-2" style={{ fontSize: "0.8rem", lineHeight: 1.6 }}>
                Semantic layers a linguistic completion check on top of voice activity to
                judge end-of-turn more accurately - auto-picks English or multilingual model
                based on this provider's selected language(s).
              </p>
            </div>
            <div className="mb-3">
              <label className="text-white form-label">Voice Provider</label>
              <select
                className="bg-black border-0 text-white py-2 px-3 form-select"
                id="selected_voice"
                value={selectedVoice}
                onChange={handleSelectedVoice}
                onFocus={resetErrorFieldStyle}
              >
                <option className="selectedProviderOption" value="">
                  Select a voice provider
                </option>
                {voices.length > 0
                  ? voices.map((voice, index) => {
                    return (
                      <option
                        key={index}
                        className="selectedProviderOption"
                        value={voice.CategoryValue}
                      >
                        {voice.CategoryValue}
                      </option>
                    );
                  })
                  : ""}
              </select>
            </div>
            <div className="mb-3">
              <label className="text-white form-label">STT Provider</label>
              <select
                className="bg-black border-0 text-white py-2 px-3 form-select"
                id="stt_provider"
                value={sttProvider}
                onChange={(e) => setSttProvider(e.target.value)}
                onFocus={resetErrorFieldStyle}
              >
                <option className="selectedProviderOption" value="Deepgram">
                  Deepgram
                </option>
                <option className="selectedProviderOption" value="Eleven Labs">
                  Eleven Labs
                </option>
              </select>
            </div>
            {sttProvider === "Deepgram" ? (
              <div className="mb-3">
                <label className="text-white form-label">Select STT Model</label>
                <select
                  className="bg-black border-0 text-white py-2 px-3 form-select"
                  id="selected_stt_model"
                  value={selectedSttModel}
                  onChange={(e) => setSelectedSttModel(e.target.value)}
                  onFocus={resetErrorFieldStyle}
                >
                  {sttModelOptions.map((model) => (
                    <option
                      key={model.value}
                      className="selectedProviderOption"
                      value={model.value}
                    >
                      {model.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <p
                className="text-white-50 mb-3"
                style={{ fontSize: "0.8rem", lineHeight: 1.6 }}
              >
                Uses ElevenLabs Scribe v2 Realtime - the only ElevenLabs STT model that
                supports live streaming and works with adaptive interruption detection.
              </p>
            )}
            {sttProvider === "Deepgram" && isFluxKeytermEnabled ? (
              <div className="mb-3">
                <label className="text-white form-label">Flux Keyterms</label>
                <textarea
                  rows="4"
                  value={fluxKeyterms}
                  onChange={(e) => setFluxKeyterms(e.target.value)}
                  className="bg-black text-white border-0 py-2 px-3 form-control"
                  placeholder="One term or phrase per line. Used only with Deepgram Flux."
                ></textarea>
                <p
                  className="text-white-50 mb-0 mt-2"
                  style={{ fontSize: "0.78rem", lineHeight: 1.6 }}
                >
                  Add provider names, medications, insurance terms, abbreviations,
                  and other specialty phrases you want Flux to hear correctly.
                </p>
              </div>
            ) : sttProvider === "Deepgram" ? (
              <p
                className="text-white-50 mb-3"
                style={{ fontSize: "0.8rem", lineHeight: 1.6 }}
              >
                Flux keyterms are available only when a Deepgram Flux STT model is
                selected.
              </p>
            ) : null}
            <div className="mb-3">
              <label className="text-white form-label">
                Voice
              </label>
              <select
                className="bg-black border-0 text-white py-2 px-3 form-select"
                id="selected_voiceModal"
                value={selectedVoiceModal}
                onChange={(e) => setSelectedVoiceModal(e.target.value)}
                onFocus={resetErrorFieldStyle}
              >
                <option className="selectedProviderOption" value="">
                  Select a voice
                </option>
                {selectedVoiceModals.length > 0
                  ? selectedVoiceModals.map((voiceModal, index) => {
                    if (
                      voiceModal.SubCategoryLabel &&
                      voiceModal.SubCategoryValue
                    ) {
                      return (
                        <option
                          key={index}
                          className="selectedProviderOption"
                          value={voiceModal.SubCategoryValue}
                        >
                          {voiceModal.SubCategoryLabel}
                        </option>
                      );
                    }
                  })
                  : ""}
              </select>
            </div>
            {selectedVoice === "Eleven Labs" ? (
              <div className="mb-3">
                <label className="text-white form-label">TTS Model</label>
                <select
                  className="bg-black border-0 text-white py-2 px-3 form-select"
                  id="eleven_labs_tts_model"
                  value={elevenLabsTtsModel}
                  onChange={(e) => setElevenLabsTtsModel(e.target.value)}
                  onFocus={resetErrorFieldStyle}
                >
                  <option className="selectedProviderOption" value="eleven_flash_v2_5">
                    Flash v2.5 (fastest, recommended)
                  </option>
                  <option className="selectedProviderOption" value="eleven_turbo_v2_5">
                    Turbo v2.5
                  </option>
                  <option className="selectedProviderOption" value="eleven_multilingual_v2">
                    Multilingual v2 (most expressive, higher latency)
                  </option>
                </select>
                <p
                  className="text-white-50 mb-0 mt-2"
                  style={{ fontSize: "0.78rem", lineHeight: 1.6 }}
                >
                  Flash and Turbo cost the same and sound nearly identical - Flash just
                  responds faster. Multilingual v2 trades some speed for more expressive,
                  emotionally nuanced speech.
                </p>
              </div>
            ) : null}
            {/* </>
                                : ""
                        } */}
            {/* ── Knowledge Base Section ──────────────────────────────── */}
            <div className="mb-3 mt-4">
              <div style={kbPanelStyle}>
                <div
                  className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4"
                  style={{
                    borderBottom: "1px solid rgba(65, 60, 88, 0.08)",
                    paddingBottom: "18px",
                  }}
                >
                  <div>
                    <h5 className="text-white mb-2" style={{ fontWeight: 700 }}>
                      Knowledge Base
                    </h5>
                    <p className="mb-0" style={kbSecondaryTextStyle}>
                      Give the agent searchable practice knowledge and clear rules
                      for when it should look things up instead of guessing.
                    </p>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <span style={kbBadgeStyle}>
                      <DescriptionOutlinedIcon style={{ fontSize: "0.95rem" }} />
                      {kbDocuments.length} document{kbDocuments.length === 1 ? "" : "s"}
                    </span>
                    <span style={kbBadgeStyle}>
                      <CheckIcon style={{ fontSize: "0.95rem" }} />
                      {kbActiveDocumentCount} active
                    </span>
                  </div>
                </div>

                <div className="d-grid gap-3">
                  <div style={kbCardStyle}>
                    <div style={kbSectionHeadingStyle}>
                      <UploadFileOutlinedIcon style={{ fontSize: "1.15rem" }} />
                      Upload and Index Documents
                    </div>
                    <p style={kbSecondaryTextStyle}>
                      Add `.txt`, `.pdf`, `.md`, or `.docx` files the agent should
                      search during live calls.
                    </p>
                    <input
                      type="file"
                      id="kbFileInput"
                      accept=".txt,.pdf,.md,.docx"
                      onChange={handleKbFileChange}
                      style={{ display: "none" }}
                    />
                    <div className="d-flex flex-column flex-lg-row gap-3 mt-3">
                      <label htmlFor="kbFileInput" style={kbGhostButtonStyle}>
                        <UploadFileOutlinedIcon style={{ fontSize: "1rem" }} />
                        Choose File
                      </label>
                      <div
                        style={{
                          flex: 1,
                          minHeight: "48px",
                          display: "flex",
                          alignItems: "center",
                          borderRadius: "14px",
                          border: "1px dashed rgba(99, 201, 220, 0.24)",
                          background: "rgba(247, 245, 242, 0.72)",
                          color: kbFile ? "#280137" : "#6d5f83",
                          padding: "0 16px",
                          fontSize: "0.85rem",
                          wordBreak: "break-word",
                        }}
                      >
                        {kbFile ? kbFile.name : "No document selected yet"}
                      </div>
                      <button
                        type="button"
                        className="simple-btn login-btn"
                        style={{
                          minWidth: "150px",
                          opacity: kbUploading || !kbFile ? 0.7 : 1,
                          cursor: kbUploading || !kbFile ? "not-allowed" : "pointer",
                        }}
                        onClick={handleKbUpload}
                        disabled={kbUploading || !kbFile}
                      >
                        {kbUploading ? (
                          <span className="spinner-border spinner-border-sm" role="status" />
                        ) : (
                          "Upload File"
                        )}
                      </button>
                    </div>
                  </div>

                  <div style={kbCardStyle}>
                    <div style={kbSectionHeadingStyle}>
                      <DescriptionOutlinedIcon style={{ fontSize: "1.15rem" }} />
                      Indexed Documents
                    </div>
                    <p style={kbSecondaryTextStyle}>
                      Review what is currently available to the agent during calls.
                    </p>
                    {kbDocuments.length > 0 ? (
                      <div className="d-grid gap-2 mt-3">
                        {kbDocuments.map((doc) => {
                          const isActive =
                            String(doc.Status || "").toLowerCase() === "active";
                          return (
                            <div
                              key={doc.Id}
                              className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3"
                              style={{
                                background: "rgba(247, 245, 242, 0.72)",
                                border: "1px solid rgba(65, 60, 88, 0.08)",
                                borderRadius: "14px",
                                padding: "14px 16px",
                              }}
                            >
                              <div style={{ minWidth: 0 }}>
                                <div
                                  className="text-white"
                                  style={{
                                    fontSize: "0.9rem",
                                    fontWeight: 600,
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {doc.FileName}
                                </div>
                                <div className="mt-2">
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "6px",
                                      borderRadius: "999px",
                                      padding: "4px 10px",
                                      fontSize: "0.72rem",
                                      fontWeight: 700,
                                      letterSpacing: "0.02em",
                                      background: isActive
                                        ? "rgba(74, 222, 128, 0.14)"
                                        : "rgba(250, 204, 21, 0.12)",
                                      color: isActive ? "#6ee7a8" : "#f5d257",
                                    }}
                                  >
                                    {isActive && (
                                      <CheckIcon style={{ fontSize: "0.85rem" }} />
                                    )}
                                    {doc.Status}
                                  </span>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleKbDelete(doc.Id)}
                                className="d-inline-flex align-items-center justify-content-center gap-2"
                                style={{
                                  borderRadius: "12px",
                                  border: "1px solid rgba(248, 113, 113, 0.2)",
                                  background: "rgba(248, 113, 113, 0.08)",
                                  color: "#ff9b9b",
                                  padding: "10px 14px",
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  alignSelf: "flex-start",
                                }}
                              >
                                <DeleteOutlineOutlinedIcon style={{ fontSize: "0.95rem" }} />
                                Remove
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div
                        className="mt-3"
                        style={{
                          borderRadius: "16px",
                          border: "1px dashed rgba(65, 60, 88, 0.12)",
                          background: "rgba(247, 245, 242, 0.72)",
                          padding: "22px 18px",
                          textAlign: "center",
                        }}
                      >
                        <DescriptionOutlinedIcon
                          style={{
                            fontSize: "1.7rem",
                            color: "rgba(147, 197, 253, 0.68)",
                            marginBottom: "10px",
                          }}
                        />
                        <p className="text-white mb-1" style={{ fontWeight: 600 }}>
                          No documents uploaded yet
                        </p>
                        <p className="mb-0" style={kbSecondaryTextStyle}>
                          Upload policies, FAQs, pricing sheets, insurance notes, or
                          workflow documents to start building the agent’s reference
                          library.
                        </p>
                      </div>
                    )}
                  </div>

                  <div style={kbCardStyle}>
                    <div style={kbSectionHeadingStyle}>
                      <AutoStoriesOutlinedIcon style={{ fontSize: "1.15rem" }} />
                      Usage Instructions
                    </div>
                    <p style={kbSecondaryTextStyle}>
                      Tell the agent exactly when to search the knowledge base. This
                      instruction is injected into the system prompt.
                    </p>
                    <textarea
                      rows={5}
                      className="bg-black text-white border-0 py-3 px-3 form-control mt-3"
                      placeholder="Example: Use the knowledge base when the caller asks about billing, insurance coverage, office policies, procedures, pricing, or any company-specific information you are not certain about."
                      value={kbUsagePrompt}
                      onChange={(e) => setKbUsagePrompt(e.target.value)}
                      style={kbTextAreaStyle}
                    />
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mt-3">
                      <p className="mb-0" style={kbSecondaryTextStyle}>
                        Best results come from concrete triggers like billing,
                        insurance, pricing, office policies, medications, and
                        provider-specific processes.
                      </p>
                      <button
                        type="button"
                        className="simple-btn login-btn"
                        style={{
                          minWidth: "168px",
                          opacity: kbSavingPrompt || !providerId ? 0.7 : 1,
                          cursor:
                            kbSavingPrompt || !providerId ? "not-allowed" : "pointer",
                        }}
                        onClick={handleKbUsagePromptSave}
                        disabled={kbSavingPrompt || !providerId}
                        title={!providerId ? "Select an assistant first" : ""}
                      >
                        {kbSavingPrompt ? (
                          <span className="spinner-border spinner-border-sm" role="status" />
                        ) : (
                          "Save Instructions"
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {kbError && (
                  <div
                    className="mt-3"
                    style={{
                      borderRadius: "14px",
                      border: "1px solid rgba(248, 113, 113, 0.18)",
                      background: "rgba(244, 63, 94, 0.08)",
                      color: "#be123c",
                      fontSize: "0.82rem",
                      padding: "12px 14px",
                    }}
                  >
                    {kbError}
                  </div>
                )}
              </div>
            </div>
            {/* ── End Knowledge Base Section ───────────────────────────── */}

            <div className="auth-review-grid mt-4">
              <div className="auth-review-card">
                <p className="auth-review-card__title">Assistant details</p>
                <dl className="auth-review-list">
                  <div className="auth-review-list__row">
                    <dt>Organization</dt>
                    <dd>{providerFacilityName || "Not added"}</dd>
                  </div>
                  <div className="auth-review-list__row">
                    <dt>Assistant</dt>
                    <dd>{providerName || "Not added"}</dd>
                  </div>
                  <div className="auth-review-list__row">
                    <dt>Inbound number</dt>
                    <dd>{providerContactNumber || "Not selected"}</dd>
                  </div>
                  <div className="auth-review-list__row">
                    <dt>Timezone</dt>
                    <dd>{selectedTimezone || "Not selected"}</dd>
                  </div>
                </dl>
              </div>
              <div className="auth-review-card">
                <p className="auth-review-card__title">Prompt configuration</p>
                <dl className="auth-review-list">
                  <div className="auth-review-list__row">
                    <dt>Model</dt>
                    <dd>{selectedModal || "Not selected"}</dd>
                  </div>
                  <div className="auth-review-list__row">
                    <dt>Voice</dt>
                    <dd>{selectedVoice || "Not selected"}</dd>
                  </div>
                  <div className="auth-review-list__row">
                    <dt>STT model</dt>
                    <dd>{selectedSttModel || "Not selected"}</dd>
                  </div>
                  <div className="auth-review-list__row">
                    <dt>Prompt file</dt>
                    <dd>{promptFile?.name || promptFiledownloadLink || "Not added"}</dd>
                  </div>
                </dl>
              </div>
            </div>
              </section>
            ) : null}

            <div className="login-action-stack auth-register-actions">
              <div className="auth-register-actions__row">
                {currentStep > 1 ? (
                  <button
                    type="button"
                    className="simple-btn register-back-btn auth-register-actions__button auth-register-actions__button--secondary"
                    onClick={previousStep}
                  >
                    Back
                  </button>
                ) : (
                  <span
                    className="auth-register-actions__button prompt-hub-action-spacer"
                    aria-hidden="true"
                  />
                )}
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  {providerId && currentStep < INBOUND_PROMPT_STEPS.length ? (
                    <button
                      type="button"
                      className="simple-btn register-back-btn auth-register-actions__button auth-register-actions__button--secondary"
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      Save changes
                    </button>
                  ) : null}
                  {currentStep < INBOUND_PROMPT_STEPS.length ? (
                    <button
                      type="button"
                      className="simple-btn login-btn form-hidden login-submit-btn auth-register-actions__button"
                      onClick={nextStep}
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      id="submitButtonForm"
                      type="button"
                      className="simple-btn login-btn form-hidden login-submit-btn auth-register-actions__button"
                      onClick={handleSubmit}
                    >
                      {loading ? (
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      ) : providerId ? (
                        "Save changes"
                      ) : (
                        "Publish"
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

